import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { PaginatedPackageCategoryList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey: (string | undefined)[];
}

interface IUsePackageCategories {
  search?: string;
  ordering?: string;
}

function usePackageCategories({
  search,
  ordering,
}: IUsePackageCategories): UseInfiniteQueryResult<AxiosResponse<PaginatedPackageCategoryList>> {
  const fetchPackageCategories = useCallback(
    ({
      pageParam = { limit: '20', offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedPackageCategoryList>> => {
      const params = pageParam;

      if (search) {
        params.search = search;
      }

      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).concat(',pk');
      }

      return client.get(`package-categories/?${encodeUrlParams(params)}`);
    },
    [ordering, search],
  );

  return useInfiniteQuery(['packageCategories', search, ordering], fetchPackageCategories, {
    getNextPageParam: page => {
      return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

export default usePackageCategories;
